/**
 * SchedulingConfirmInspector
 * ─────────────────────────────────────────────────────────────────────────
 * Lightweight, right-side confirmation panel that opens AFTER a meaningful
 * scheduling action in PlanCockpit:
 *   • Job dropped from Ready Rail → grid (new placement)
 *   • Conflict resolution applied
 *   • Major reschedule (date or crew change for an existing booking)
 *
 * Goal: give the operator instant confidence about what was just scheduled
 * — who, when, on what crew, how ready, what risks — without leaving the
 * cockpit. This is a CONTEXTUAL CONFIRMATION layer, not a job detail view.
 *
 * Reuses (no new logic):
 *   - derivePreSpendGate          → pre-spend gate snapshot
 *   - deriveJobState              → install / payment / follow-up state
 *   - useSchedulingConflicts      → conflicts on this job
 *   - capacityEngine grid         → crew row + utilization context
 *   - DropAnalysis                → warnings/impacts captured at commit time
 *   - PushDrawer                  → standard right-side shell
 *
 * Does not duplicate job-detail; only scheduling-critical information.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { format, parseISO, addDays, isValid, differenceInDays } from "date-fns";
import {
  CheckCircle2, AlertTriangle, XCircle, MapPin, User, Wrench,
  Calendar, ArrowRight, ExternalLink, ChevronRight, ShieldAlert, Phone, Tag,
} from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { derivePreSpendGate } from "@/data/preSpendGate";
import { deriveJobState } from "@/data/jobStateDerived";
import { useSchedulingConflicts } from "@/hooks/useSchedulingConflicts";
import { useScheduling } from "@/data/scheduling/context";
import { getJobRiskSignals, countCrewRiskyJobs } from "@/data/scheduling/riskSignals";
import { buildSchedulingSuggestions } from "@/data/scheduling/suggestions";
import type { CapacityGrid } from "@/data/scheduling/capacityEngine";
import type { DropAnalysis } from "@/data/scheduling/dropAnalyzer";
import { PushDrawer } from "@/components/quick-actions/PushDrawer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, ArrowRightLeft, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { getKit, KIT_OVERRIDE_REASONS } from "@/data/materialKit";
import { assertJobKitReady } from "@/data/kitDispatchGuard";
import { assertMaterialAlignment, summarizeAlignment } from "@/data/materialAlignmentGate";
import { useOrderDraft } from "@/data/OrderDraftContext";
import { useVendorPO } from "@/data/VendorPOContext";
import { isExternalManagedImport } from "@/data/import/migrationVisibilityGuard";
import { displayIdentity } from "@/lib/uuid";

export type InspectorTrigger = "drop_from_rail" | "conflict_resolved" | "reschedule";

export interface SchedulingInspectorContext {
  jobId: string;
  trigger: InspectorTrigger;
  /** Captured at commit time so the panel reflects the action (not stale state). */
  analysis?: DropAnalysis | null;
  warningNote?: string | null;
  /**
   * Optional pre-fill from a "Better match available" suggestion in
   * CellPeekPopover. The Inspector renders an explicit "Switch crew" CTA;
   * the operator confirms the swap. No inline mutation occurs upstream.
   */
  proposedCrew?: string | null;
}

export interface SchedulingConfirmInspectorProps {
  context: SchedulingInspectorContext | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  grid: CapacityGrid;
  onExtend: (jobId: string) => void;
  onReschedule: (jobId: string) => void;
  onReturnToQueue: (jobId: string) => void;
  onResolveConflict: (jobId: string) => void;
  /**
   * Performs the actual crew swap when the operator accepts a "Better
   * match available" suggestion. Confirmation happens here in the
   * Inspector — never inline in the popover.
   */
  onSwitchCrew?: (jobId: string, crewName: string) => void;
}

function projectEndDate(startIso: string, shifts: number): string {
  let cursor = parseISO(startIso);
  let placed = 1;
  while (placed < shifts) {
    cursor = addDays(cursor, 1);
    if (cursor.getDay() === 0 || cursor.getDay() === 6) continue;
    placed += 1;
  }
  return format(cursor, "yyyy-MM-dd");
}

type Tone = "ok" | "warn" | "block" | "muted";

function ReadinessRow({
  icon, label, status, detail, tone,
}: {
  icon: React.ReactNode;
  label: string;
  status: string;
  detail?: string;
  tone: Tone;
}) {
  const dotCls =
    tone === "ok"    ? "bg-success" :
    tone === "warn"  ? "bg-warning" :
    tone === "block" ? "bg-destructive" :
                       "bg-muted-foreground/40";
  const statusCls =
    tone === "ok"    ? "text-success" :
    tone === "warn"  ? "text-warning" :
    tone === "block" ? "text-destructive" :
                       "text-muted-foreground";
  return (
    <div className="flex items-start gap-3 py-2">
      <span className="mt-0.5 text-muted-foreground/70 shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[12px] font-medium text-foreground">{label}</p>
          <span className={cn("inline-flex items-center gap-1.5 text-[11px] tabular-nums", statusCls)}>
            <span className={cn("h-1.5 w-1.5 rounded-full", dotCls)} />
            {status}
          </span>
        </div>
        {detail && (
          <p className="text-[10.5px] text-muted-foreground mt-0.5 leading-snug">{detail}</p>
        )}
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground mb-2">
      {children}
    </p>
  );
}

export function SchedulingConfirmInspector({
  context, open, onOpenChange, grid,
  onExtend, onReschedule, onReturnToQueue, onResolveConflict, onSwitchCrew,
}: SchedulingConfirmInspectorProps) {
  const { jobs, issues, installerWrapRecords, salesOpportunities, appointmentResults, materialKits } = useAppData();
  const finPerms = useFinancialPermissions();
  const conflicts = useSchedulingConflicts(jobs);
  const { eventLog, auditTrail } = useScheduling();
  const { drafts: orderDrafts } = useOrderDraft();
  const { purchaseOrders } = useVendorPO();

  const job = useMemo(
    () => (context ? jobs.find(j => j.id === context.jobId) ?? null : null),
    [context, jobs],
  );
  const externallyManaged = useMemo(() => (job ? isExternalManagedImport(job) : false), [job]);

  // Always call hooks — derive null-safe values when job missing.
  const gate = useMemo(() => {
    if (!job) return null;
    if (externallyManaged) return { cleared: true, checks: [], shortReason: "Externally managed import" };
    return derivePreSpendGate(job);
  }, [job, externallyManaged]);
  const derived = useMemo(
    () => job ? deriveJobState(job, issues, installerWrapRecords) : null,
    [job, issues, installerWrapRecords],
  );
  const jobConflicts = useMemo(
    () => context ? conflicts.all.filter(c => c.jobId === context.jobId) : [],
    [conflicts, context],
  );

  // ── Lightweight risk signals (read-only over scheduling event log) ──
  // Hooks MUST run before the early-return guard below — derive null-safe
  // values when job/context are missing so hook count is stable.
  const risk = useMemo(
    () => job ? getJobRiskSignals(job.id, eventLog, auditTrail) : { reschedules: 0, extensions: 0, conflicts: 0, hasAny: false },
    [job, eventLog, auditTrail],
  );
  const _assigneeForRisk = job
    ? (job.assignmentType === "Subcontractor" ? job.assignedSubcontractor : job.assignedCrew)
    : undefined;
  const crewRiskyJobsCount = useMemo(() => {
    if (!job || !_assigneeForRisk) return 0;
    const scheduled = jobs.filter(j => j.id !== job.id && !!j.scheduledInstallDate);
    return countCrewRiskyJobs(_assigneeForRisk, scheduled, eventLog, auditTrail);
  }, [_assigneeForRisk, jobs, job, eventLog, auditTrail]);

  // ── Lightweight Recommendation Layer ─────────────────────────────
  // Up to 2 suggestions in priority order: Better Crew → Risk-aware →
  // Crew Load. The pre-fill from a popover ("proposedCrew") promotes the
  // matching Better Crew suggestion to the top regardless of evaluation.
  const suggestions = useMemo(() => {
    if (!job) return [];
    return buildSchedulingSuggestions({
      job,
      currentCrew: _assigneeForRisk,
      dateIso: job.scheduledInstallDate || null,
      grid,
      jobs,
      eventLog,
      auditTrail,
      max: 2,
      includeCrewLoad: true,
    });
  }, [job, _assigneeForRisk, grid, jobs, eventLog, auditTrail]);

  // If the popover passed a proposedCrew but the suggestion engine didn't
  // surface it (e.g. user clicked an older preview that's no longer better),
  // we still want to honor the operator's intent: render an explicit swap CTA.
  const explicitProposedCrew = context?.proposedCrew && context.proposedCrew !== _assigneeForRisk
    ? context.proposedCrew
    : null;

  if (!context || !job) {
    return (
      <PushDrawer
        open={open}
        onOpenChange={onOpenChange}
        header={<p className="text-[13px] font-medium text-foreground">Scheduling Confirmation</p>}
      >
        <p className="text-[12px] text-muted-foreground">No job selected.</p>
      </PushDrawer>
    );
  }

  const assignee = job.assignmentType === "Subcontractor"
    ? job.assignedSubcontractor
    : job.assignedCrew;
  const crewRow = grid.rows.find(r => r.crewName === assignee) ?? null;

  const startIso = job.scheduledInstallDate;
  const startValid = !!startIso && isValid(parseISO(startIso));
  const shifts = Math.max(1, job.scheduledShifts ?? 1);
  const endIso = startValid ? projectEndDate(startIso, shifts) : null;

  const arrivalTime = job.timeSlot && job.timeSlot.trim().length > 0
    ? job.timeSlot
    : "Not set";

  // ── Readiness rows (reuse existing derived state) ────────────────────
  const reviewCheck = gate!.checks.find(c => c.name === "review");
  const financialCheck = gate!.checks.find(c => c.name === "financial");

  const matStatus = job.materialReadiness;
  const matTone: Tone =
    matStatus === "Loaded for Crew" || matStatus === "Staged for Job" || matStatus === "Fully Received" ? "ok" :
    matStatus === "Ordered" || matStatus === "Partially Received" ? "warn" :
    "block";

  const permitTone: Tone =
    !job.permitRequired || job.permitStatus === "Not Required" ? "muted" :
    job.permitStatus === "Approved" ? "ok" :
    job.permitStatus === "Submitted" ? "warn" :
    "block";

  const customerTone: Tone = job.customerSignoff ? "ok" : "muted";

  // ── Material Kit dispatch readiness (visibility) ─────────────────────
  // Uses the SAME predicate the actual assignment paths enforce —
  // assertJobKitReady → isKitGateCleared. No duplicate logic.
  const kit = getKit(materialKits, job.id);
  const kitVerdict = assertJobKitReady({ kit });
  const kitTone: Tone =
    kitVerdict.state === "ok" ? "ok" :
    kitVerdict.state === "ok_override" ? "warn" :
    "block";
  const kitStatusLabel =
    kitVerdict.state === "ok" ? "Confirmed" :
    kitVerdict.state === "ok_override" ? "Override logged" :
    kitVerdict.state === "not_generated" ? "Not generated" :
    "Incomplete";
  const overrideReasonLabel = kit?.override
    ? KIT_OVERRIDE_REASONS.find(r => r.id === kit.override!.reason)?.label ?? kit.override.reason
    : null;

  // ── Material Alignment (visibility) ──────────────────────────────────
  // Same predicate the dispatch gate enforces. Read-only here — surfaces
  // Aligned / Out of Sync / Missing PO / Missing Kit so the dispatcher
  // sees what the install gate sees BEFORE assignment, not after.
  const alignmentVerdict = assertMaterialAlignment(job, { kit, orderDrafts, purchaseOrders });
  const alignmentTone: Tone =
    alignmentVerdict.ok ? "ok" :
    alignmentVerdict.missing.length > 0 ? "warn" :
    "block";
  const alignmentStatusLabel =
    alignmentVerdict.ok ? "Aligned" :
    alignmentVerdict.missing.includes("po") ? "Missing PO" :
    alignmentVerdict.missing.includes("kit") ? "Missing Kit" :
    alignmentVerdict.missing.includes("spec") ? "Spec not bound" :
    "Out of Sync";
  const alignmentDetail = alignmentVerdict.ok
    ? undefined
    : summarizeAlignment(alignmentVerdict);

  // ── Upstream lifecycle context (read-only, single lookups) ──
  const opp = job.opportunityId
    ? salesOpportunities.find(o => o.id === job.opportunityId)
    : undefined;
  const apptResult = job.appointmentResultId
    ? appointmentResults.find(r => r.id === job.appointmentResultId)
    : undefined;
  // All appointment results for this opportunity → simple "history" summary.
  const apptHistory = opp
    ? appointmentResults.filter(r => r.opportunityId === opp.id)
    : [];
  const apptSummary = (() => {
    if (!opp && !apptResult) return null;
    if (apptHistory.length === 0 && !apptResult) return null;
    const total = apptHistory.length;
    const reschedules = apptHistory.filter(r => r.resultType === "rescheduled").length;
    const sold = apptResult ?? apptHistory.find(r => r.resultType === "sold");
    if (sold && total <= 1) return "Sold on first visit";
    if (sold && reschedules > 0) return `Sold after ${reschedules} reschedule${reschedules === 1 ? "" : "s"}`;
    if (sold) return `Sold · ${total} appointment${total === 1 ? "" : "s"}`;
    return `${total} appointment${total === 1 ? "" : "s"}`;
  })();
  const saleAgeDays = opp?.wonAt
    ? differenceInDays(new Date(), parseISO(opp.wonAt))
    : null;
  const inRescission = job.rescissionStatus === "In Rescission";
  const rescissionEnds = job.rescissionEndsAt && isValid(parseISO(job.rescissionEndsAt))
    ? parseISO(job.rescissionEndsAt)
    : null;
  const showPayout = finPerms.viewCrewPayout && job.payoutAmount > 0;
  const hasUpstream = !!(job.salesRep || opp?.leadSource || saleAgeDays != null
    || job.scopeSummary || apptSummary || showPayout
    || job.secondaryContactName || job.secondaryContactPhone || inRescission);

  const triggerLabel =
    context.trigger === "drop_from_rail"  ? "Job placed" :
    context.trigger === "conflict_resolved" ? "Conflict resolved" :
                                              "Job rescheduled";

  // ── Warnings / impacts (reuse analyzer + persisted note) ────────────
  const warnings: string[] = [];
  if (context.analysis?.verdict === "warning") {
    warnings.push(context.analysis.headline);
    for (const r of context.analysis.reasons) warnings.push(r);
  } else if (context.warningNote) {
    warnings.push(context.warningNote);
  }
  if (context.analysis?.travel === "high") {
    warnings.push("Out-of-area assignment — long travel for this crew.");
  }
  if (context.analysis && context.analysis.newUtilizationPct >= 100) {
    warnings.push(`Crew at ${context.analysis.newUtilizationPct}% capacity this week.`);
  } else if (context.analysis && context.analysis.newUtilizationPct >= 90) {
    warnings.push(`Crew under pressure (${context.analysis.newUtilizationPct}% utilization).`);
  }

  const fitScore = context.analysis?.fitScore ?? null;
  const fitLabel = fitScore == null ? null
    : fitScore >= 85 ? "strong fit"
    : fitScore >= 70 ? "good fit"
    : fitScore >= 50 ? "marginal"
    :                  "poor fit";

  const overallReady = externallyManaged
    ? jobConflicts.length === 0
    : gate!.cleared && jobConflicts.length === 0 && (matTone === "ok") && (permitTone !== "block");

  return (
    <PushDrawer
      open={open}
      onOpenChange={onOpenChange}
      widthClass="sm:max-w-md"
      header={
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.16em] font-medium text-muted-foreground">
            {triggerLabel}
          </p>
          <p className="text-[14px] font-semibold text-foreground truncate mt-0.5">
            {job.customerName}
          </p>
          <p className="text-[11px] text-muted-foreground truncate mt-0.5">
            {displayIdentity(job.jobNumber || job.id, "JOB")} · {job.projectType}
          </p>
        </div>
      }
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="text-muted-foreground"
          >
            Close
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onReturnToQueue(job.id)}>
              Return to queue
            </Button>
            <Button variant="outline" size="sm" onClick={() => onReschedule(job.id)}>
              Reschedule
            </Button>
            <Button size="sm" onClick={() => onExtend(job.id)}>
              Extend
            </Button>
          </div>
        </div>
      }
      bodyClassName="space-y-5"
    >
      {/* ── Overall verdict banner ─────────────────────────────────── */}
      <div
        className={cn(
          "flex items-start gap-3 px-3 py-2.5 rounded-md border",
          overallReady
            ? "border-success/40 bg-success/[0.06]"
            : warnings.length > 0 || !gate!.cleared
              ? "border-warning/40 bg-warning/[0.06]"
              : "border-border bg-muted/30",
        )}
      >
        {overallReady ? (
          <CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[12px] font-semibold text-foreground">
            {overallReady ? "Ready to install" : "Review before install"}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {overallReady
              ? "All gates cleared. No conflicts on this booking."
              : externallyManaged
                ? "External-managed import. Native readiness gates are informational only."
                : !gate!.cleared
                ? gate!.shortReason
                : warnings.length > 0
                  ? "Booked with caveats — see warnings below."
                  : "Some readiness items still open."}
          </p>
        </div>
      </div>

      {/* ── Suggestions strip (Lightweight Recommendation Layer) ──
           Up to 2 high-confidence hints derived purely from already-
           computed fit + risk signals. Better Crew renders an explicit
           "Switch crew" CTA — operator confirms here, no inline swap. */}
      {(suggestions.length > 0 || explicitProposedCrew) && (
        <div className="space-y-1.5">
          {explicitProposedCrew && !suggestions.some(s => s.kind === "better_crew" && s.proposedCrew === explicitProposedCrew) && (
            <div className="flex items-start gap-2 px-3 py-2 rounded-md border border-primary/40 bg-primary/[0.06]">
              <Sparkles className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[11.5px] text-foreground leading-snug">
                  Suggested crew: <span className="font-medium">{explicitProposedCrew}</span>
                </p>
              </div>
              {onSwitchCrew && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 px-2 text-[11px] shrink-0"
                  onClick={() => onSwitchCrew(job.id, explicitProposedCrew)}
                >
                  <ArrowRightLeft className="h-3 w-3 mr-1" />
                  Switch
                </Button>
              )}
            </div>
          )}
          {suggestions.map((s, i) => {
            const isBetterCrew = s.kind === "better_crew" && s.proposedCrew;
            const toneCls = s.tone === "warning"
              ? "border-warning/40 bg-warning/[0.06]"
              : "border-primary/40 bg-primary/[0.06]";
            const iconCls = s.tone === "warning" ? "text-warning" : "text-primary";
            return (
              <div key={`${s.kind}-${i}`} className={cn("flex items-start gap-2 px-3 py-2 rounded-md border", toneCls)}>
                <Sparkles className={cn("h-3.5 w-3.5 mt-0.5 shrink-0", iconCls)} />
                <p className="text-[11.5px] text-foreground leading-snug flex-1 min-w-0">
                  {s.message}
                </p>
                {isBetterCrew && onSwitchCrew && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[11px] shrink-0"
                    onClick={() => onSwitchCrew(job.id, s.proposedCrew!)}
                  >
                    <ArrowRightLeft className="h-3 w-3 mr-1" />
                    Switch
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Crew Suggestion Bias v1 — non-redundant guidance: only when the job
          is risky AND the currently selected crew is already loaded with
          risky jobs. Reuses existing `risk` and `crewRiskyJobsCount` memos. */}
      {risk.hasAny && crewRiskyJobsCount >= 2 && (
        <p className="text-[11px] text-muted-foreground/80 leading-snug -mt-0.5">
          Prefer stable crew for this job
        </p>
      )}

      {/* ── 1. Job / Homeowner ─────────────────────────────────────── */}
      <div>
        <SectionLabel>Homeowner</SectionLabel>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 text-[12px] text-foreground">
            <User className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-medium">{job.customerName}</span>
          </div>
          <div className="flex items-start gap-2 text-[11.5px] text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span className="leading-snug">
              {job.address.street ? `${job.address.street}, ` : ""}
              {job.address.city}{job.address.state ? `, ${job.address.state}` : ""}
            </span>
          </div>
          {risk.hasAny && (
            <div className="flex flex-wrap gap-1 pt-0.5">
              {risk.reschedules >= 2 && (
                <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-medium border-warning/40 text-warning">
                  Rescheduled {risk.reschedules}×
                </Badge>
              )}
              {risk.extensions >= 1 && (
                <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-medium border-warning/40 text-warning">
                  Previously extended
                </Badge>
              )}
              {risk.conflicts >= 1 && (
                <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-medium border-destructive/40 text-destructive">
                  Conflict-prone
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── 1b. Upstream lifecycle context (sales → install) ─────── */}
      {hasUpstream && (
        <div>
          <SectionLabel>Upstream context</SectionLabel>
          <div className="border border-border/50 rounded-md px-3 py-2 space-y-1.5 text-[11.5px]">
            {(job.salesRep || saleAgeDays != null) && (
              <div className="flex items-center gap-2 text-foreground">
                <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="min-w-0 truncate">
                  {job.salesRep && <span className="font-medium">{job.salesRep}</span>}
                  {job.salesRep && saleAgeDays != null && <span className="text-muted-foreground"> · </span>}
                  {saleAgeDays != null && (
                    <span className="text-muted-foreground tabular-nums">signed {saleAgeDays}d ago</span>
                  )}
                </span>
              </div>
            )}
            {opp?.leadSource && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Tag className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate"><span className="text-foreground/80">Source:</span> {opp.leadSource}</span>
              </div>
            )}
            {job.scopeSummary && (
              <div className="flex items-start gap-2 text-muted-foreground">
                <Wrench className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span className="leading-snug" title={job.scopeSummary}>
                  <span className="text-foreground/80">Scope:</span> {job.scopeSummary}
                </span>
              </div>
            )}
            {apptSummary && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate"><span className="text-foreground/80">Appointments:</span> {apptSummary}</span>
              </div>
            )}
            {(job.secondaryContactName || job.secondaryContactPhone) && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Phone className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">
                  <span className="text-foreground/80">Alt contact:</span>{" "}
                  {[job.secondaryContactName, job.secondaryContactPhone].filter(Boolean).join(" · ")}
                </span>
              </div>
            )}
            {showPayout && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="text-foreground/80">Crew payout:</span>
                <span className="text-foreground font-medium tabular-nums">
                  ${job.payoutAmount.toLocaleString()}
                </span>
              </div>
            )}
            {inRescission && (
              <div className="flex items-center gap-2 text-warning font-medium">
                <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
                <span>
                  In rescission{rescissionEnds ? ` · clears ${format(rescissionEnds, "MMM d")}` : ""}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── 2. Scheduling details ───────────────────────────────── */}
      <div>
        <SectionLabel>Scheduling</SectionLabel>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12px]">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Date</p>
            <p className="text-foreground font-medium tabular-nums">
              {startValid ? format(parseISO(startIso), "EEE MMM d") : "—"}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Arrival</p>
            <p className={cn(
              "tabular-nums",
              arrivalTime === "Not set" ? "text-muted-foreground italic" : "text-foreground font-medium",
            )}>
              {arrivalTime}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Crew</p>
            <p className="text-foreground font-medium truncate">
              {assignee || "—"}
            </p>
            {crewRow && (
              <p className="text-[10px] text-muted-foreground tabular-nums">
                {crewRow.utilizationPct}% utilization this week
              </p>
            )}
            {/* High-risk load was previously shown here as a tertiary line.
                It is now consolidated into the Suggestions strip at the top
                of the panel — keeping a single source of risk awareness. */}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Span</p>
            <p className="text-foreground font-medium tabular-nums">
              {shifts} shift{shifts === 1 ? "" : "s"}
            </p>
            {startValid && endIso && shifts > 1 && (
              <p className="text-[10px] text-muted-foreground tabular-nums flex items-center gap-1">
                {format(parseISO(startIso), "MMM d")}
                <ArrowRight className="h-2.5 w-2.5" />
                {format(parseISO(endIso), "MMM d")}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── 3. Readiness snapshot ──────────────────────────────── */}
      <div>
        <SectionLabel>Readiness</SectionLabel>
        <div className="divide-y divide-border/40 border border-border/50 rounded-md px-3">
          {externallyManaged ? (
            <>
              <ReadinessRow
                icon={<CheckCircle2 className="h-3.5 w-3.5" />}
                label="Readiness ownership"
                status="External managed"
                detail="Upstream readiness (review/financial/materials) is managed in source system."
                tone="muted"
              />
              <ReadinessRow
                icon={<Package className="h-3.5 w-3.5" />}
                label="Material checks"
                status="Bypassed"
                detail="Native material alignment/kit gates are bypassed for this imported job."
                tone="muted"
              />
            </>
          ) : (
            <>
              <ReadinessRow
                icon={<CheckCircle2 className="h-3.5 w-3.5" />}
                label="Build Review"
                status={reviewCheck?.cleared ? "Approved" : "Pending"}
                detail={reviewCheck?.detail}
                tone={reviewCheck?.cleared ? "ok" : "block"}
              />
              <ReadinessRow
                icon={<CheckCircle2 className="h-3.5 w-3.5" />}
                label="Financial / Pre-spend"
                status={financialCheck?.cleared ? "Cleared" : "Blocked"}
                detail={financialCheck?.detail}
                tone={financialCheck?.cleared ? "ok" : "block"}
              />
              <ReadinessRow
                icon={<Wrench className="h-3.5 w-3.5" />}
                label="Materials"
                status={matStatus || "—"}
                detail={job.materialReadinessNotes || job.missingItemsList || undefined}
                tone={matTone}
              />
              <ReadinessRow
                icon={<Package className="h-3.5 w-3.5" />}
                label="Material Kit (JobSpec)"
                status={kitStatusLabel}
                detail={
                  kitVerdict.state === "ok_override" && kit?.override
                    ? `${overrideReasonLabel} · ${kit.override.decidedBy} · ${format(parseISO(kit.override.decidedAt), "MMM d")}`
                    : kitVerdict.state === "ok"
                      ? undefined
                      : kitVerdict.reason
                }
                tone={kitTone}
              />
            </>
          )}
          {kitVerdict.state === "ok_override" && kit?.override && (
            <div className="py-2 -mx-3 px-3 bg-warning/[0.04] border-t border-warning/20">
              <div className="flex items-start gap-2 text-[11px] text-warning/90">
                <Package className="h-3 w-3 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="font-medium">PM override active</p>
                  <p className="text-muted-foreground leading-snug">
                    Reason: <span className="text-foreground/80">{overrideReasonLabel}</span>
                  </p>
                  <p className="text-muted-foreground tabular-nums">
                    {kit.override.decidedBy} · {format(parseISO(kit.override.decidedAt), "MMM d, h:mma")}
                  </p>
                  {kit.override.notes && (
                    <p className="text-muted-foreground leading-snug pt-0.5">
                      "{kit.override.notes}"
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          <ReadinessRow
            icon={<Package className="h-3.5 w-3.5" />}
            label="Material Alignment"
            status={externallyManaged ? "Bypassed" : alignmentStatusLabel}
            detail={externallyManaged ? "External-managed import bypasses native material alignment gates." : alignmentDetail}
            tone={externallyManaged ? "muted" : alignmentTone}
          />
          <ReadinessRow
            icon={<Calendar className="h-3.5 w-3.5" />}
            label="Permit"
            status={
              !job.permitRequired ? "Not required" :
              job.permitStatus || "—"
            }
            tone={permitTone}
          />
          <ReadinessRow
            icon={<User className="h-3.5 w-3.5" />}
            label="Customer confirmation"
            status={job.customerSignoff ? "Confirmed" : "Not yet confirmed"}
            tone={customerTone}
          />
        </div>
      </div>

      {/* ── 4. Warnings / impacts ───────────────────────────────── */}
      {(warnings.length > 0 || jobConflicts.length > 0) && (
        <div>
          <SectionLabel>Warnings & impacts</SectionLabel>
          <div className="space-y-1.5">
            {jobConflicts.map(c => (
              <button
                key={`${c.type}-${c.jobId}`}
                type="button"
                onClick={() => onResolveConflict(job.id)}
                className="w-full flex items-start gap-2 px-3 py-2 rounded-md border border-destructive/40 bg-destructive/[0.06] hover:bg-destructive/[0.1] transition-colors text-left"
              >
                <XCircle className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[11.5px] font-medium text-foreground">{c.label}</p>
                  <p className="text-[10.5px] text-muted-foreground">{c.detail}</p>
                </div>
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground mt-0.5" />
              </button>
            ))}
            {warnings.map((w, i) => (
              <div
                key={i}
                className="flex items-start gap-2 px-3 py-2 rounded-md border border-warning/40 bg-warning/[0.06]"
              >
                <AlertTriangle className="h-3.5 w-3.5 text-warning mt-0.5 shrink-0" />
                <p className="text-[11.5px] text-foreground leading-snug">{w}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 5. Fit + state context ─────────────────────────────── */}
      {(fitScore != null || derived) && (
        <div>
          <SectionLabel>Context</SectionLabel>
          <div className="flex items-center gap-3 text-[11.5px]">
            {fitScore != null && (
              <span className="inline-flex items-center gap-1.5 text-foreground tabular-nums">
                <span className="text-muted-foreground">Fit</span>
                <span className="font-semibold">{fitScore}</span>
                <span className="text-muted-foreground">· {fitLabel}</span>
              </span>
            )}
            {derived && (
              <span className="text-muted-foreground truncate">
                · {derived.summary}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── 6. Open full job ────────────────────────────────────── */}
      <div className="pt-1">
        <Link
          to={`/jobs/${job.id}`}
          className="inline-flex items-center gap-1.5 text-[11.5px] text-primary hover:underline"
        >
          Open full job
          <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
    </PushDrawer>
  );
}
